import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { F0Dialog, F0Form, f0FormField, useF0Form } from '@factorialco/f0-react'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { handleResult } from 'modules/core/lib/handleMutation'
import { trackingEvents } from 'modules/core/lib/Tracking/events'
import { track } from 'modules/core/lib/Tracking/tracker'
import { SelectionMetadata } from 'modules/core/components/EntitySelectorV2/types'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { useCreateModuleRequest } from 'modules/marketplace_modules/hooks/useCreateModuleRequest'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

type RequestModuleFormValues = { message?: string }

interface Props {
  isOpen: boolean
  onClose: () => void
  moduleName: string
  moduleSlug: string
}

const RequestModuleModalContent = ({
  onClose,
  moduleName,
  moduleSlug,
}: {
  onClose: () => void
  moduleName: string
  moduleSlug: string
}) => {
  const { pathname } = useLocation()
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([])
  const { formRef, submit } = useF0Form()
  const { create, loading } = useCreateModuleRequest()
  const {
    access: { locale: user_locale },
  } = useCurrentSession()

  const handleClose = () => {
    setSelectedEmployeeIds([])
    onClose()
  }

  const handleEmployeeChange = (
    state: EmployeeSelectorV2MultiSelectState,
    _metadata: SelectionMetadata
  ) => {
    if (state.type === 'manual') {
      setSelectedEmployeeIds(state.ids)
    }
  }

  const handleSubmit = async (values: RequestModuleFormValues) => {
    const result = await create({
      moduleName,
      moduleSlug,
      recipientEmployeeIds: selectedEmployeeIds,
      message: values.message || undefined,
      userLocale: user_locale,
    })
    const handled = handleResult(result, () => {})

    if (!handled.success) {
      return { success: false }
    }

    handleClose()
    track(trackingEvents.marketplaceModules.submitEmployeeModuleRequestModal, {
      module: moduleSlug,
      origin: pathname,
    })
    app.setFlash({
      type: 'correct',
      message: i18n.t('marketplace.revamp.request_module_modal.request_sent'),
    })
    return { success: true }
  }

  const schema = useMemo(
    () =>
      z.object({
        message: f0FormField(z.string().optional(), {
          label: i18n.t('marketplace.revamp.request_module_modal.message_label'),
          placeholder: i18n.t('marketplace.revamp.request_module_modal.message_placeholder'),
          fieldType: 'textarea',
          rows: 4,
        }),
      }),
    []
  )

  return (
    <F0Dialog
      isOpen
      width="sm"
      title={i18n.t('marketplace.revamp.request_module_modal.title')}
      onClose={handleClose}
      primaryAction={{
        label: i18n.t('marketplace.revamp.request_module_modal.send'),
        onClick: submit,
        disabled: selectedEmployeeIds.length === 0 || loading,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: handleClose,
      }}
    >
      <div className="flex flex-col gap-6">
        <p className="text-base text-f1-foreground-secondary">
          {i18n.t('marketplace.revamp.request_module_modal.description')}
        </p>
        <EmployeeSelectorV2
          value={selectedEmployeeIds}
          onChange={handleEmployeeChange}
          multiple
          mandatory
          label={i18n.t('marketplace.revamp.request_module_modal.employee_label')}
          forcedFilters={{ withVerb: 'ManageMarketplaceModule' }}
          filters={[]}
          options={{ disableSelectAll: true }}
          messages={{
            searchEmptyMessage: i18n.t(
              'marketplace.revamp.request_module_modal.employee_empty_results'
            ),
          }}
        />
        <F0Form
          formRef={formRef}
          name="request-module"
          schema={schema}
          defaultValues={{ message: '' }}
          submitConfig={{ type: 'default', hideSubmitButton: true, hideActionBar: true }}
          onSubmit={handleSubmit}
        />
      </div>
    </F0Dialog>
  )
}

export const RequestModuleModal = ({ isOpen, onClose, moduleName, moduleSlug }: Props) => {
  if (!isOpen) return null

  return (
    <RequestModuleModalContent onClose={onClose} moduleName={moduleName} moduleSlug={moduleSlug} />
  )
}

export default RequestModuleModal
